import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { Link } from "react-router-dom";

interface Announcement {
  id: number;
  title: string;
  message: string;
  createdAt: string;
  priority: string;
}

export function AnnouncementsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["/api/parent/announcements"],
    queryFn: () => api.get("/api/parent/announcements"),
  });

  const announcements: Announcement[] = (data?.data || []).slice(0, 3);

  if (isLoading) {
    return (
      <Card data-testid="parent-announcements-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Announcements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="parent-announcements-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Announcements
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {announcements.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="announcements-card-empty">
            No new announcements
          </p>
        ) : (
          <>
            <div className="space-y-3" data-testid="announcements-card-list">
              {announcements.map((announcement) => (
                <div 
                  key={announcement.id} 
                  className="border-l-2 border-blue-500 pl-3 py-1"
                  data-testid={`announcements-card-item-${announcement.id}`}
                >
                  <h4 className="font-medium text-sm" data-testid={`announcements-card-title-${announcement.id}`}>
                    {announcement.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(announcement.createdAt), "MMM dd")}
                  </p>
                </div>
              ))}
            </div>
            <Link to="/parent/announcements">
              <Button variant="outline" size="sm" className="w-full" data-testid="announcements-card-view-all">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
